// Country & currency utilities for CholloMan

export const COUNTRIES = {
  ES: {
    code: 'ES',
    name: 'España',
    // Se conserva por si algo lo necesita en texto plano, pero la interfaz
    // dibuja la bandera con <Bandera code=...>: Windows no trae los glifos
    // de los indicadores regionales y aquí se veían las letras del código.
    flag: '🇪🇸',
    currency: 'EUR',
    symbol: '€',
    locale: 'es-ES',
    defaultStore: null,
    // España vuelve al servicio: el catálogo se alimenta del extractor de
    // canales de Telegram (`ML_API/extractor_telegram_es.py`), que trae ofertas
    // de Amazon, MediaMarkt, El Corte Inglés, Lidl, AliExpress y demás.
    //
    // `defaultStore` pasa a null: con veinte tiendas no tiene sentido arrancar
    // filtrado por una. Antes era 'Zara', de cuando el catálogo eran tres.
    disponible: true,
  },
  AR: {
    code: 'AR',
    name: 'Argentina',
    // Se conserva por si algo lo necesita en texto plano, pero la interfaz
    // dibuja la bandera con <Bandera code=...>: Windows no trae los glifos
    // de los indicadores regionales y aquí se veían las letras del código.
    flag: '🇦🇷',
    // Primary currency is ARS; international stores may use USD or EUR
    currency: 'ARS',
    symbol: '$',
    locale: 'es-AR',
    defaultStore: null,
    disponible: true,
  },
};

/** Todo lo que el modelo conoce, esté o no en servicio. */
export const SUPPORTED_COUNTRY_CODES = Object.keys(COUNTRIES);

/** Lo que la interfaz puede ofrecer hoy. Es la lista que manda. */
export const AVAILABLE_COUNTRY_CODES = Object.keys(COUNTRIES).filter(
  code => COUNTRIES[code].disponible
);

export const AVAILABLE_COUNTRIES = AVAILABLE_COUNTRY_CODES.map(code => COUNTRIES[code]);

/** ¿Se puede navegar este país ahora mismo? */
export const esPaisDisponible = (code) =>
  AVAILABLE_COUNTRY_CODES.includes(String(code || '').toUpperCase());

/**
 * País por defecto cuando no hay nada mejor: Argentina, que es el catálogo
 * completo y el foco del desarrollo. Antes el respaldo era España, así que un
 * visitante con zona horaria no reconocible aterrizaba en la versión vacía.
 */
export const DEFAULT_COUNTRY = 'AR';

/**
 * Secciones que existen sólo en el catálogo argentino.
 *
 *   - `viajes` → `telegram_extractor.py` lee canales de promos aéreas
 *                argentinos. Para España no hay fuente equivalente.
 *
 * Se ocultan en vez de mostrarse vacías: una sección sin contenido se lee como
 * que el sitio está roto. El día que aparezca una fuente española, se saca de
 * esta lista y vuelve sola.
 *
 * `exclusivos` estuvo acá hasta que España tuvo su propio catálogo: ahora sale
 * de `productos_es` (Mango Outlet, vía `ML_API/main_es.py`) igual que en AR
 * sale de `productos_ar`. Es la misma sección con dos fuentes distintas.
 */
export const SECCIONES_SOLO_AR = ['viajes'];

/** ¿Esta sección se puede mostrar en este país? */
export const seccionDisponible = (seccion, code) =>
  !SECCIONES_SOLO_AR.includes(seccion) ||
  String(code || '').toUpperCase() === 'AR';

/**
 * Detecta el país por la zona horaria del navegador (sin API externa, inmediato).
 *
 * Sólo puede devolver países en servicio, y con España reactivada eso ya son
 * los dos: quien entra desde Madrid aterriza en el catálogo español. El filtro
 * por `disponible` se mantiene igual, que es lo que permitió que reactivar
 * España fuera cambiar una bandera y nada más.
 */
export function detectCountryFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    let detectado = null;
    if (tz.startsWith('America/Argentina') || tz === 'America/Buenos_Aires') detectado = 'AR';
    else if (tz === 'Europe/Madrid' || tz === 'Atlantic/Canary' || tz === 'Africa/Ceuta') detectado = 'ES';
    else if (tz.startsWith('Europe/')) detectado = 'ES';
    else if (tz.startsWith('America/')) detectado = 'AR';
    if (detectado && esPaisDisponible(detectado)) return detectado;
  } catch {
    // Intl no disponible
  }
  return DEFAULT_COUNTRY;
}

const STORAGE_KEY = 'cholloman_country';

// Nombre del parámetro en la URL. Va como query (?pais=ar) y no dentro del
// hash porque el hash ya se usa para página y tienda: mezclar los tres hacía
// la URL ilegible, que es justo lo contrario de lo que se busca.
const URL_PARAM = 'pais';

/**
 * País indicado explícitamente en la URL, si lo hay.
 * Tiene prioridad sobre lo guardado y sobre la detección: un enlace compartido
 * debe abrirse en el país que dice el enlace.
 */
export function getCountryFromUrl(search) {
  try {
    const params = new URLSearchParams(
      search !== undefined ? search : window.location.search
    );
    const valor = (params.get(URL_PARAM) || '').trim().toUpperCase();
    // Un enlace viejo con ?pais=es no debe abrir un catálogo retirado: se
    // ignora y la resolución sigue hasta dar con uno en servicio.
    if (esPaisDisponible(valor)) return valor;
  } catch {
    // URLSearchParams no disponible
  }
  return null;
}

/**
 * Refleja el país en la URL sin ensuciar el historial: se usa `replaceState`
 * porque cambiar de país no es un paso de navegación que el botón "atrás"
 * deba deshacer paso por paso.
 */
export function setCountryInUrl(code) {
  if (!esPaisDisponible(code)) return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(URL_PARAM) === code.toLowerCase()) return;
    url.searchParams.set(URL_PARAM, code.toLowerCase());
    window.history.replaceState(null, '', url.toString());
  } catch {
    // History API no disponible
  }
}

export function getStoredCountry() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    // Una preferencia guardada antes de retirar España tampoco vale ya.
    if (stored && esPaisDisponible(stored)) return stored;
  } catch {}
  return null;
}

export function storeCountry(code) {
  try { localStorage.setItem(STORAGE_KEY, code); } catch {}
}

/**
 * País activo, por orden de prioridad:
 *   1. el de la URL      — un enlace compartido manda sobre todo lo demás
 *   2. el guardado       — la elección previa del usuario
 *   3. la zona horaria   — la detección automática
 *
 * Los tres pasos descartan países fuera de servicio, así que el resultado
 * siempre es un país navegable.
 */
export function resolveCountry() {
  const resuelto = getCountryFromUrl() || getStoredCountry() || detectCountryFromTimezone();
  return esPaisDisponible(resuelto) ? resuelto : DEFAULT_COUNTRY;
}

/**
 * ¿El usuario ya dijo alguna vez en qué catálogo quiere estar?
 *
 * Cuenta tanto lo guardado como lo que venga en el enlace. Sirve para decidir
 * si preguntar en la primera visita: a quien ya eligió no se le vuelve a
 * preguntar nunca — para cambiar está el selector del encabezado.
 */
export function hayEleccionPrevia() {
  return Boolean(getCountryFromUrl() || getStoredCountry());
}

/**
 * Qué se detectó y con cuánta certeza, para poder explicarlo.
 *
 * `detectCountryFromTimezone()` sólo devuelve AR o ES porque son los dos
 * catálogos que existen, y eso deja un caso incómodo: quien entra desde Italia
 * o desde México aterriza en uno de los dos sin saber por qué. La detección no
 * puede hacerlo mejor —no hay catálogo italiano— pero sí puede **decirlo**, y
 * de eso se ocupa `zona`.
 *
 * `exacta` distingue «tu zona horaria es Madrid» de «estás en Europa, así que
 * te mostramos España». Con la primera no hace falta justificar nada; con la
 * segunda, sí.
 */
export function describirDeteccion() {
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    // Intl no disponible: se cae al país por defecto sin más explicación.
  }

  const pais = detectCountryFromTimezone();

  if (tz.startsWith('America/Argentina') || tz === 'America/Buenos_Aires') {
    return { pais, zona: 'Argentina', exacta: true };
  }
  if (tz === 'Europe/Madrid' || tz === 'Atlantic/Canary' || tz === 'Africa/Ceuta') {
    return { pais, zona: 'España', exacta: true };
  }
  if (tz.startsWith('Europe/')) return { pais, zona: 'Europa', exacta: false };
  if (tz.startsWith('America/')) return { pais, zona: 'América', exacta: false };
  return { pais, zona: null, exacta: false };
}

const CURRENCY_LOCALE = {
  EUR: 'es-ES',
  ARS: 'es-AR',
  USD: 'en-US',
};

/**
 * Format a price string using the currency stored in the DB.
 * Price and currency are never derived from the active country — they come from the product.
 *
 * @param {string|number} priceStr  - raw price from DB (e.g. "€12,99", "$1250", "12.99")
 * @param {string} moneda           - currency code stored in DB: 'EUR' | 'ARS' | 'USD'
 * @returns {string} formatted price
 */
export function formatPrice(priceStr, moneda) {
  if (!priceStr) return null;

  const str = String(priceStr).replace(/[€$£\s]/g, '');
  const hasDot   = str.includes('.');
  const hasComma = str.includes(',');

  let num;
  if (hasDot && hasComma) {
    // European format: "1.299,99" → dot=thousands, comma=decimal
    num = parseFloat(str.replace(/\./g, '').replace(',', '.'));
  } else if (hasComma && !hasDot) {
    // Comma-only decimal: "19,99"
    num = parseFloat(str.replace(',', '.'));
  } else {
    // Standard decimal from DB: "26593.20" or "26593"
    num = parseFloat(str);
  }

  if (isNaN(num)) return String(priceStr);

  const currency = moneda || 'EUR';
  const locale   = CURRENCY_LOCALE[currency] || 'es-ES';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'ARS' ? 0 : 2,
      minimumFractionDigits: 0,
    }).format(num);
  } catch {
    const sym = currency === 'EUR' ? '€' : currency === 'USD' ? 'US$' : '$';
    return `${sym}${num.toLocaleString(locale)}`;
  }
}

/**
 * Parse a price string to a plain number (strips all currency symbols).
 */
export function parsePrice(priceStr) {
  if (!priceStr) return 0;
  try {
    const str = String(priceStr).replace(/[€$£\s]/g, '');
    const hasDot   = str.includes('.');
    const hasComma = str.includes(',');
    if (hasDot && hasComma) return parseFloat(str.replace(/\./g, '').replace(',', '.')) || 0;
    if (hasComma && !hasDot) return parseFloat(str.replace(',', '.')) || 0;
    return parseFloat(str) || 0;
  } catch {
    return 0;
  }
}

/**
 * Currency label for display (short form).
 */
export function getCurrencyLabel(moneda) {
  const map = { EUR: '€', ARS: 'ARS $', USD: 'US$' };
  return map[moneda] || moneda || '€';
}
